import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Paperclip, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { DateInput } from '@/components/ui/date-input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useAdminConsentTypes } from '@/features/admin-consent-types/api';
import {
  useConsentMutations,
  type ConsentInput,
} from '@/features/patient-record/hooks/useConsentMutations';
import { useDirtyDialogGuard } from '@/features/patient-record/hooks/useDirtyDialogGuard';
import { useEditableDraft } from '@/features/patient-record/hooks/useEditableDraft';
import { useUploadDocument } from '@/features/patient-record/hooks/useDocumentMutations';
import type {
  ConsentStatus,
  ConsentViewModel,
} from '@/features/patient-record/types';

import { FormField } from '../_shared/FormField';

// WITHDRAWN is intentionally omitted from both create and edit — a consent can
// only reach it via the signed → withdrawn transition on
// `POST /persons/{guid}/consents/{id}/withdraw`; a create/PATCH with status
// WITHDRAWN returns 422. Withdrawal is driven by the row's Withdraw action.
const SELECTABLE_STATUSES: readonly ConsentStatus[] = ['PENDING', 'SIGNED', 'DECLINED'];

interface ConsentFormProps {
  readonly guid: string;
  readonly consent?: ConsentViewModel;
  readonly onClose: () => void;
}

interface ConsentDraft {
  readonly consentType: string;
  readonly status: ConsentStatus;
  readonly signedAt: string | null;
  readonly signedBy: string | null;
  readonly notes: string | null;
  /** File to attach on submit. Only used on create — the edit flow reuses the
   *  paperclip affordance on the existing row so we don't have to unify two
   *  attach paths inside the form. */
  readonly file: File | null;
}

const toDraft = (consent?: ConsentViewModel): ConsentDraft =>
  consent !== undefined
    ? {
        consentType: consent.consentType,
        status: consent.status,
        signedAt: consent.signedAt,
        signedBy: consent.signedBy,
        notes: consent.notes,
        file: null,
      }
    : {
        consentType: '',
        status: 'PENDING',
        signedAt: null,
        signedBy: null,
        notes: null,
        file: null,
      };

const toInput = (d: ConsentDraft): ConsentInput => ({
  consentType: d.consentType,
  status: d.status,
  signedAt: d.signedAt,
  signedBy: d.signedBy,
  notes: d.notes,
});

export function ConsentForm({
  guid,
  consent,
  onClose,
}: ConsentFormProps): ReactElement {
  const { t } = useTranslation();
  const { add, patch, setDocumentRef } = useConsentMutations(guid);
  const upload = useUploadDocument(guid);
  const catalogue = useAdminConsentTypes('true');

  const isEdit = consent !== undefined;
  const label = t('patient.consent.formLabel', { defaultValue: 'Consent' });
  const pending =
    add.isPending || patch.isPending || upload.isPending || setDocumentRef.isPending;

  // Fields the PATCH must touch: the draft diffed against the saved row. Empty in create mode.
  const changedFields = (d: ConsentDraft): (keyof ConsentInput)[] => {
    if (!isEdit) return [];
    const changed: (keyof ConsentInput)[] = [];
    if (d.consentType !== consent.consentType) changed.push('consentType');
    if (d.status !== consent.status) changed.push('status');
    if ((d.signedAt ?? null) !== consent.signedAt) changed.push('signedAt');
    if ((d.signedBy ?? null) !== consent.signedBy) changed.push('signedBy');
    if ((d.notes ?? null) !== consent.notes) changed.push('notes');
    return changed;
  };

  // Commit AND close — the explicit Add/Save button (and, for create, the nav-guard teardown flush).
  const submitAndClose = (d: ConsentDraft): void => {
    if (d.consentType.trim().length === 0) return;
    if (!isEdit) {
      // Two-step when a file is attached: create the consent first, then
      // upload the file, then link it. The consent is created either way
      // — if the upload fails partway, the row already exists and the
      // clinician can retry from the paperclip affordance on the row.
      add.mutate(toInput(d), {
        onSuccess: (created) => {
          if (d.file === null) {
            onClose();
            return;
          }
          upload.mutate(
            {
              title: t('patient.consent.attachment.uploadTitle', {
                name: created.consentType,
              }),
              documentType: 'DOCUMENT',
              file: d.file,
            },
            {
              onSuccess: (attachment) => {
                setDocumentRef.mutate(
                  { id: created.id, documentRef: attachment.id },
                  { onSettled: onClose },
                );
              },
              // If upload fails we still close — the consent is created and
              // the user can attach from the row. The mutation surfaces its
              // own error toast.
              onError: () => {
                onClose();
              },
            },
          );
        },
      });
      return;
    }
    // Diff against the row so the PATCH only touches changed fields.
    const changed = changedFields(d);
    if (changed.length === 0) {
      onClose();
      return;
    }
    patch.mutate({ id: consent.id, input: toInput(d), changed }, { onSuccess: onClose });
  };

  // The primitive owns the draft + dirty registration. Both create and edit commit only on the
  // explicit Add/Save button (autosave off); onSave here is reached solely via the nav-guard teardown
  // flush. A picked file still counts toward `isDirty` via the file-aware `isEqual`.
  const form = useEditableDraft<ConsentDraft>({
    initial: toDraft(consent),
    label,
    autosave: false,
    isEqual: (a, b) =>
      a.file === b.file &&
      a.consentType === b.consentType &&
      a.status === b.status &&
      (a.signedAt ?? null) === (b.signedAt ?? null) &&
      (a.signedBy ?? null) === (b.signedBy ?? null) &&
      (a.notes ?? null) === (b.notes ?? null),
    canSave: (d) => d.consentType.trim().length > 0,
    onSave: (d) => {
      if (!isEdit) {
        // Create is explicit-only; onSave is only reached here via the nav-guard teardown flush,
        // where closing is moot (the surface is unmounting) — reuse the full create + attach path.
        submitAndClose(d);
        return undefined;
      }
      const changed = changedFields(d);
      if (changed.length === 0) return undefined;
      return patch.mutateAsync({ id: consent.id, input: toInput(d), changed });
    },
  });
  const draft = form.draft;
  const canSubmit = draft.consentType.trim().length > 0 && !pending;

  // Merge the current row's type into the picker even if it's been
  // soft-disabled since the record was signed — otherwise editing an
  // existing consent would show an empty select for a valid legacy value.
  const options = useMemo(() => {
    const names = new Set<string>();
    for (const row of catalogue.data ?? []) if (row.isActive) names.add(row.name);
    if (isEdit && consent.consentType.length > 0) names.add(consent.consentType);
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [catalogue.data, isEdit, consent?.consentType]);

  const signedAtDate = draft.signedAt !== null ? draft.signedAt.slice(0, 10) : '';

  const guard = useDirtyDialogGuard({
    dirty: form.isDirty,
    canSave: form.canSave,
    saving: pending,
    label,
    onSave: () => {
      submitAndClose(draft);
    },
    onClose,
    register: false,
  });

  return (
    <>
      <Dialog open {...guard.rootProps}>
        <DialogContent
          className="sm:max-w-lg max-h-[calc(100vh-3rem)] p-0 gap-0 flex flex-col"
          {...guard.contentProps}
        >
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-border shrink-0">
            <DialogTitle>
              {isEdit ? t('patient.consent.edit') : t('patient.consent.addConsent')}
            </DialogTitle>
          </DialogHeader>
          <form
            ref={form.containerProps.ref}
            className="flex flex-col min-h-0 flex-1"
            onSubmit={(event) => {
              event.preventDefault();
              submitAndClose(draft);
            }}
          >
            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-3">
              <FormField label={t('patient.consent.consentType')} required>
                <Select
                  value={draft.consentType}
                  onChange={(event) => {
                    form.setDraft((d) => ({ ...d, consentType: event.target.value }));
                  }}
                  disabled={catalogue.isPending}
                >
                  <option value="" disabled>
                    {catalogue.isPending
                      ? t('patient.consent.consentTypeLoading')
                      : t('patient.consent.consentTypePlaceholder')}
                  </option>
                  {options.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </Select>
              </FormField>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                <FormField label={t('patient.consent.statusLabel')}>
                  <Select
                    value={draft.status}
                    onChange={(event) => {
                      form.setDraft((d) => ({ ...d, status: event.target.value as ConsentStatus }));
                    }}
                  >
                    {SELECTABLE_STATUSES.map((status) => (
                      <option key={status} value={status}>
                        {t(`patient.consent.status.${status}`)}
                      </option>
                    ))}
                  </Select>
                </FormField>
                <FormField label={t('patient.consent.signedAtLabel')}>
                  <DateInput
                    value={signedAtDate}
                    onChange={(value) => {
                      form.setDraft((d) => ({
                        ...d,
                        signedAt:
                          value.length > 0
                            ? new Date(`${value}T12:00:00`).toISOString()
                            : null,
                      }));
                    }}
                  />
                </FormField>
              </div>
              <FormField label={t('patient.consent.signedByLabel')}>
                <Input
                  type="text"
                  value={draft.signedBy ?? ''}
                  onChange={(event) => {
                    const next = event.target.value;
                    form.setDraft((d) => ({ ...d, signedBy: next.length > 0 ? next : null }));
                  }}
                />
              </FormField>
              <FormField label={t('patient.consent.notesLabel')}>
                <Textarea
                  value={draft.notes ?? ''}
                  rows={2}
                  placeholder={t('patient.consent.notesPlaceholder')}
                  onChange={(event) => {
                    const next = event.target.value;
                    form.setDraft((d) => ({ ...d, notes: next.length > 0 ? next : null }));
                  }}
                />
              </FormField>
              {/* Attach on create only. Edit reuses the paperclip chip on the row so the upload flow
                  has a single canonical entry point. Rendered as a plain <div>+<label> pair — FormField
                  wraps its children in a <label> and nesting labels is invalid HTML. */}
              {!isEdit && (
                <div className="space-y-1.5">
                  <p className="text-xs font-medium">
                    {t('patient.consent.attachment.formLabel')}
                  </p>
                  {draft.file === null ? (
                    <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-input bg-background px-3 py-2 text-xs text-muted-foreground hover:bg-accent hover:text-accent-foreground">
                      <Paperclip className="h-3.5 w-3.5" aria-hidden="true" />
                      {t('patient.consent.attachment.formPick')}
                      <input
                        type="file"
                        className="hidden"
                        onChange={(event) => {
                          const picked = event.target.files?.[0] ?? null;
                          event.target.value = '';
                          form.setDraft((d) => ({ ...d, file: picked }));
                        }}
                      />
                    </label>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded border border-border/60 bg-background pl-1.5 pr-0.5 py-0.5 text-xs text-muted-foreground max-w-full">
                      <Paperclip className="h-3 w-3 shrink-0" aria-hidden="true" />
                      <span className="min-w-0 truncate">{draft.file.name}</span>
                      <button
                        type="button"
                        onClick={() => {
                          form.setDraft((d) => ({ ...d, file: null }));
                        }}
                        aria-label={t('patient.consent.attachment.formClear')}
                        title={t('patient.consent.attachment.formClear')}
                        className="ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
                      >
                        <X className="h-3 w-3" aria-hidden="true" />
                      </button>
                    </span>
                  )}
                </div>
              )}
            </div>
            <DialogFooter className="shrink-0 border-t border-border px-6 py-3">
              <Button
                type="button"
                variant="ghost"
                onClick={guard.requestClose}
                disabled={pending}
              >
                {t('patient.consent.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={!canSubmit}
                loading={pending}
                loadingText={t('patient.dirtyFooter.saving')}
              >
                {isEdit ? t('patient.consent.save') : t('patient.consent.addConsent')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      {guard.confirm}
    </>
  );
}
